import type { Material } from '@/lib/types'

/**
 * CSV import/export of the material catalog — a port of `MaterialCsv.kt`, deliberately identical so
 * a file written by the app imports here and vice versa.
 *
 * Format: a header row plus one row per material, columns
 * `code,name,category,unit,description,isActive,isPrivate,name_en,name_az,description_en,description_az`.
 * Fields containing a comma, quote or newline are wrapped in double quotes with quotes doubled; line
 * endings are CRLF. The four translation columns are optional on import, so older files still work.
 */
export const CSV_HEADER = [
  'code',
  'name',
  'category',
  'unit',
  'description',
  'isActive',
  'isPrivate',
  'name_en',
  'name_az',
  'description_en',
  'description_az',
] as const

export type MaterialCsvRow = {
  code: string
  name: string
  category: string
  unit: string
  description: string
  isActive: boolean
  isPrivate: boolean
  nameI18n: Record<string, string>
  descriptionI18n: Record<string, string>
}

/** The material's stored code, or one derived from its id when the code is blank (legacy items). */
export function materialCodeFromId(id: string): string {
  return id.slice(-12)
}

export function effectiveCode(material: Material): string {
  const code = material.code.trim()
  return code.length > 0 ? code : materialCodeFromId(material.id)
}

export function formatMaterialsCsv(materials: Material[]): string {
  const lines = [CSV_HEADER.map(escape).join(',')]
  for (const material of materials) {
    lines.push(
      [
        effectiveCode(material),
        material.name,
        material.category,
        material.unit,
        material.description,
        String(material.isActive),
        String(material.isPrivate),
        material.nameI18n.en ?? '',
        material.nameI18n.az ?? '',
        material.descriptionI18n.en ?? '',
        material.descriptionI18n.az ?? '',
      ]
        .map(escape)
        .join(','),
    )
  }
  // Trailing CRLF, like the Kotlin writer.
  return `${lines.join('\r\n')}\r\n`
}

export function parseMaterialsCsv(text: string): MaterialCsvRow[] {
  const records = parseRecords(text.replace(/^\uFEFF/, ''))
  if (records.length === 0) return []
  const hasHeader = (records[0]?.[0] ?? '').trim().toLowerCase() === 'code'
  return records
    .slice(hasHeader ? 1 : 0)
    .filter((columns) => columns.some((value) => value.trim().length > 0))
    .map((columns) => ({
      code: (columns[0] ?? '').trim(),
      name: (columns[1] ?? '').trim(),
      category: (columns[2] ?? '').trim(),
      unit: (columns[3] ?? '').trim(),
      description: (columns[4] ?? '').trim(),
      isActive: parseBool(columns[5] ?? 'true'),
      isPrivate: parseBool(columns[6] ?? 'true'),
      nameI18n: translationsOf(columns[7] ?? '', columns[8] ?? ''),
      descriptionI18n: translationsOf(columns[9] ?? '', columns[10] ?? ''),
    }))
}

function translationsOf(en: string, az: string): Record<string, string> {
  const result: Record<string, string> = {}
  if (en.trim().length > 0) result.en = en.trim()
  if (az.trim().length > 0) result.az = az.trim()
  return result
}

function escape(value: string): string {
  return /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value
}

/** Same permissive truthiness as the app: only these spellings mean "false". */
function parseBool(raw: string): boolean {
  return !['false', '0', 'no', 'n', 'нет', 'xeyr', 'yox'].includes(raw.trim().toLowerCase())
}

/** Tokenizes CSV text into records of fields, honouring quoted fields. */
function parseRecords(text: string): string[][] {
  const records: string[][] = []
  let field = ''
  let row: string[] = []
  let inQuotes = false
  let index = 0

  while (index < text.length) {
    const char = text[index]!
    if (inQuotes) {
      if (char === '"' && text[index + 1] === '"') {
        field += '"'
        index += 2
      } else if (char === '"') {
        inQuotes = false
        index += 1
      } else {
        field += char
        index += 1
      }
      continue
    }
    switch (char) {
      case '"':
        inQuotes = true
        index += 1
        break
      case ',':
        row.push(field)
        field = ''
        index += 1
        break
      case '\r':
        index += 1 // ignored; '\n' terminates the record
        break
      case '\n':
        row.push(field)
        records.push(row)
        row = []
        field = ''
        index += 1
        break
      default:
        field += char
        index += 1
    }
  }

  row.push(field)
  // Drop the empty record produced by a final newline.
  if (!(row.length === 1 && row[0] === '')) records.push(row)
  return records
}
